import { useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { FONT_MONO } from '../theme';
import { Card } from '../ui';
import { CardTitle, EmptyNote, MoreButton, extraColors } from './common';
import { PR_LABELS, prMonthCounts, recentPrs, recordGroups, shortDate } from './statsData';

/* PRs used to exist only as a toast. This card keeps them: a month count, a
 * strip of the latest PRs and a records table per muscle, all derived from
 * the logs by logic/records (the same rules Claude's get_records uses). */
export function PrWallCard({ t, isDark, records, today, exerciseMeta, selected, onSelect }) {
  const [showOlder, setShowOlder] = useState(false);
  const c = extraColors(isDark);
  const counts = useMemo(() => prMonthCounts(records.events, today), [records.events, today]);
  const recent = useMemo(() => recentPrs(records.events, 12), [records.events]);
  const { groups, olderCount } = useMemo(
    () => recordGroups(records.table, exerciseMeta, { today, showAll: showOlder }),
    [records.table, exerciseMeta, today, showOlder]
  );

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t} sub={
        <span>
          PRs this month <b style={{ color: t.text, fontFamily: FONT_MONO, fontSize: 13 }}>{counts.thisMonth}</b>
          {' '}(last month {counts.lastMonth})
        </span>
      }>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Trophy size={14} color={c.gold} /> PR wall
        </span>
      </CardTitle>

      {recent.length === 0 ? (
        <EmptyNote t={t}>
          No PRs yet. From an exercise's 4th session on, a heavier set, more reps at a weight or a higher e1RM lands here.
        </EmptyNote>
      ) : (
        <div style={{
          display: 'flex', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory',
          // scrollPadding keeps the snapped first card in line with the card text.
          margin: '0 -16px', padding: '2px 16px 10px', scrollPadding: '0 16px', WebkitOverflowScrolling: 'touch',
        }}>
          {recent.map(e => (
            <button key={`${e.date}|${e.exercise}|${e.set.weight}|${e.set.reps}`} type="button"
              onClick={() => onSelect(e.exercise)} style={{
                flex: '0 0 144px', scrollSnapAlign: 'start', textAlign: 'left',
                background: t.surfaceAlt, border: `1px solid ${e.exercise === selected ? t.accentBorder : t.cardBorder}`,
                borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 4, minHeight: 118,
              }}>
              <span style={{
                alignSelf: 'flex-start', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: c.gold, background: c.goldSoft, borderRadius: 999, padding: '2px 7px',
              }}>{PR_LABELS[e.type] || 'PR'}</span>
              <span style={{
                fontSize: 12, fontWeight: 600, color: t.text, lineHeight: 1.3,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{e.exercise}</span>
              <span style={{ fontSize: 16, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>
                {e.set.weight} × {e.set.reps}
              </span>
              {e.e1rmGain > 0 && (
                <span style={{ fontSize: 11, fontWeight: 600, color: t.accent }}>+{e.e1rmGain} kg e1RM</span>
              )}
              <span style={{ fontSize: 11, color: t.textFaint, marginTop: 'auto' }}>{shortDate(e.date)}</span>
            </button>
          ))}
        </div>
      )}

      {groups.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <RecordGrid t={t} header>
            <span>Exercise</span>
            <span style={{ textAlign: 'right' }}>e1RM</span>
            <span style={{ textAlign: 'right' }}>Best set</span>
            <span style={{ textAlign: 'right' }}>Last PR</span>
          </RecordGrid>
          {groups.map(g => (
            <div key={g.muscle}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase',
                padding: '10px 8px 4px',
              }}>{g.muscle}</div>
              {g.rows.map(r => (
                <RecordRow key={r.exercise} t={t} c={c} row={r} active={r.exercise === selected} onSelect={onSelect} />
              ))}
            </div>
          ))}
          {olderCount > 0 && (
            <MoreButton t={t} onClick={() => setShowOlder(v => !v)}>
              {showOlder ? 'Hide' : 'Show'} {olderCount} not trained in the last 8 weeks
            </MoreButton>
          )}
          <div style={{ fontSize: 11, color: t.textFaint, marginTop: 10, lineHeight: 1.5 }}>
            e1RM is an estimate from sets of 1-12 reps. <span style={{ color: c.amber }}>Amber</span>: no PR
            for 4+ weeks and 3+ sessions. Tap a row to chart it.
          </div>
        </div>
      )}
    </Card>
  );
}

function RecordGrid({ t, header, children, style, ...rest }) {
  const Tag = header ? 'div' : 'button';
  return (
    <Tag {...rest} style={{
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 50px 66px 58px', gap: 8, alignItems: 'center',
      width: '100%', padding: header ? '0 8px 4px' : '6px 8px', textAlign: 'left',
      fontSize: header ? 10 : 13, color: header ? t.textFaint : t.text,
      fontWeight: header ? 700 : 400, letterSpacing: header ? '0.04em' : 0,
      textTransform: header ? 'uppercase' : 'none',
      borderBottom: header ? `1px solid ${t.divider}` : 'none',
      ...style,
    }}>{children}</Tag>
  );
}

function lastPrText(r) {
  if (r.lastPrDate) return r.daysSinceLastPr === 0 ? 'today' : `${r.daysSinceLastPr}d ago`;
  // The first 3 sessions only set the baseline; calling that "none" would nag.
  return r.sessions <= 3 ? 'new' : 'none';
}

function RecordRow({ t, c, row: r, active, onSelect }) {
  const stale = r.stale === true;
  return (
    <RecordGrid t={t} type="button" onClick={() => onSelect(r.exercise)}
      title={stale ? `No PR in ${r.sessionsSinceLastPr} sessions` : undefined}
      style={{
        minHeight: 44, borderRadius: 8, cursor: 'pointer',
        background: active ? t.surfaceAlt : 'transparent',
        border: `1px solid ${active ? t.accentBorder : 'transparent'}`,
      }}>
      <span style={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>{r.exercise}</span>
      <span style={{ textAlign: 'right', fontFamily: FONT_MONO, fontWeight: 700, fontSize: 13, color: r.bestE1rm ? t.text : t.textFaint }}>
        {r.bestE1rm ? r.bestE1rm.e1rm : (r.assisted ? 'n/a' : '–')}
      </span>
      <span style={{ textAlign: 'right', fontFamily: FONT_MONO, fontSize: 12, color: t.textMuted, whiteSpace: 'nowrap' }}>
        {r.bestSet ? `${r.bestSet.weight}×${r.bestSet.reps}` : '–'}
      </span>
      <span style={{
        textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap',
        color: stale ? c.amber : (r.lastPrDate ? t.textMuted : t.textFaint),
        fontWeight: stale ? 700 : 500,
      }}>
        {lastPrText(r)}
      </span>
    </RecordGrid>
  );
}
